import React, { useContext, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, AuthContext } from '@/App';
import { Upload, RefreshCw, Trash2, ImageOff } from 'lucide-react';

/**
 * Ops-only image upload/preview/replace/remove control.
 * - resource: 'customers' | 'service-locations'
 * - field:    'logo' | 'image'  (matches the backend sub-path)
 * - entityId: record id (null while creating -> upload disabled)
 * - hasImage: whether the record already has an asset stored
 * - variant:  'logo' (square, contain) | 'wide' (16:9, cover)
 */
export const ImageUploadField = ({
  resource, field, entityId, hasImage = false, variant = 'logo',
  label, hint, onChange, testidPrefix = 'img',
}) => {
  const { token } = useContext(AuthContext);
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [present, setPresent] = useState(hasImage);
  const [bust, setBust] = useState(Date.now());

  const base = `${API}/${resource}/${entityId}/${field}`;
  const src = `${base}?auth=${token}&t=${bust}`;
  const square = variant === 'logo';

  const pick = () => inputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image exceeds 5MB limit'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(base, fd, { headers: { Authorization: `Bearer ${token}` } });
      setPresent(true); setBust(Date.now());
      toast.success(`${label} uploaded`);
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not upload ${label.toLowerCase()}`);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await axios.delete(base, { headers: { Authorization: `Bearer ${token}` } });
      setPresent(false);
      toast.success(`${label} removed`);
      onChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Could not remove ${label.toLowerCase()}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-field`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-wider text-[#8FC93A]">{label}</span>
        {hint && <span className="text-[11px] text-slate-400">{hint}</span>}
      </div>

      {!entityId ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500"
          data-testid={`${testidPrefix}-save-first`}>
          Save this record first, then add a {label.toLowerCase()}.
        </div>
      ) : (
        <div className="flex items-center gap-4">
          <div className={`relative overflow-hidden rounded-lg border border-slate-200 bg-white shrink-0 ${square ? 'h-24 w-24' : 'h-24 w-40'}`}>
            {present ? (
              <img src={src} alt={label}
                className={`h-full w-full ${square ? 'object-contain p-1.5' : 'object-cover'}`}
                data-testid={`${testidPrefix}-preview`}
                onError={(ev) => { ev.currentTarget.style.display = 'none'; }} />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-slate-300" data-testid={`${testidPrefix}-empty`}>
                <ImageOff size={26} />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp"
              className="hidden" onChange={upload} data-testid={`${testidPrefix}-input`} />
            <button type="button" onClick={pick} disabled={busy} data-testid={`${testidPrefix}-upload-btn`}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0E2A47] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#16324F] disabled:opacity-50">
              {present ? <RefreshCw size={14} /> : <Upload size={14} />}
              {busy ? 'Working…' : (present ? 'Replace' : 'Upload')}
            </button>
            {present && (
              <button type="button" onClick={remove} disabled={busy} data-testid={`${testidPrefix}-remove-btn`}
                className="inline-flex items-center gap-1.5 rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                <Trash2 size={14} /> Remove
              </button>
            )}
            <span className="text-[11px] text-slate-400">PNG, JPG, GIF or WEBP · max 5MB</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
